'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useSafeSearchParams } from '@/hooks/useSafeSearchParams';

const PROMO_STORAGE_KEY = 'co_promo_code';
const PROMO_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface StoredPromo {
  code: string;
  capturedAt: number;
}

export function getStoredPromoCode(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(PROMO_STORAGE_KEY);
    if (!raw) return null;

    const stored: StoredPromo = JSON.parse(raw);
    if (!stored.code || !stored.capturedAt) return null;

    // Check if expired
    if (Date.now() - stored.capturedAt > PROMO_MAX_AGE_MS) {
      localStorage.removeItem(PROMO_STORAGE_KEY);
      return null;
    }

    return stored.code;
  } catch {
    return null;
  }
}

export function clearStoredPromoCode(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(PROMO_STORAGE_KEY);
}

export default function PromoCodeCapture() {
  const pathname = usePathname();
  const searchParams = useSafeSearchParams();

  useEffect(() => {
    const promo = searchParams?.get('promo');
    if (!promo) return;

    const code = promo.trim().toUpperCase();
    if (!code) return;

    const stored: StoredPromo = {
      code,
      capturedAt: Date.now(),
    };

    try {
      localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // localStorage full or unavailable
    }
  }, [pathname, searchParams]);

  return null;
}
